import React, { useEffect, useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { Box, Button, CircularProgress, MenuItem, Radio, Select, Typography } from '@mui/material'
import { getRestaurantProfile, setSelfDeliverySchedule } from '../../apollo'
import { PF } from '../../views/profileTheme'

const GET_PROFILE = gql`${getRestaurantProfile}`
const SET_SCHEDULE = gql`${setSelfDeliverySchedule}`

// Start times every half hour, as local minutes after midnight.
const TIMES = Array.from({ length: 48 }, (_, i) => i * 30)
const DEFAULT_FROM = 14 * 60

export const clockLabel = m => {
  const h = Math.floor(m / 60)
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

// The three ways to run self delivery. Outside them, RushMyFood drivers deliver.
const OPTIONS = [
  { mode: 'DAILY', title: 'Every day', lead: 'from', tail: 'until closing', allowNow: false },
  { mode: 'TODAY', title: 'Only today', lead: 'from', tail: 'until closing tonight', allowNow: true },
  { mode: 'ALWAYS', title: 'Until I turn it off', lead: 'starting', tail: '', allowNow: true },
]

const selectSx = {
  height: 36,
  minWidth: 128,
  fontSize: 14,
  fontFamily: PF.font,
  bgcolor: PF.card,
  borderRadius: '8px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: PF.inputBorder },
}

/**
 * When the restaurant's own drivers deliver, shown under the Self Delivery
 * switch. `draft` = the switch was just turned on and nothing is saved yet.
 */
export default function SelfDeliveryPanel({ restaurantId, restaurant, draft, onCancel, onSaved }) {
  const savedMode = !draft && restaurant?.selfDelivery ? restaurant.selfDeliveryMode || 'ALWAYS' : null
  const savedFrom = savedMode && restaurant.selfDeliveryFromMinute != null ? restaurant.selfDeliveryFromMinute : 'now'

  const [mode, setMode] = useState(savedMode || 'DAILY')
  const [from, setFrom] = useState(savedMode ? savedFrom : DEFAULT_FROM)
  const [error, setError] = useState('')
  const [mutate, { loading }] = useMutation(SET_SCHEDULE, { refetchQueries: [GET_PROFILE] })

  // A refetch brings the saved setting back in.
  useEffect(() => {
    if (!savedMode) return
    setMode(savedMode)
    setFrom(savedFrom)
  }, [savedMode, savedFrom])

  const dirty = !savedMode || mode !== savedMode || from !== savedFrom

  // The start a row shows while it is not picked, and so the one it takes when
  // it is: the saved start for the saved row, else "Now" where a row allows it
  // ("Every day" needs a clock time). Carrying the last row's time over made
  // "Until I turn it off" start at 2:00 PM while its row said "Now".
  const startFor = option => {
    if (option.mode === savedMode && (option.allowNow || savedFrom !== 'now')) return savedFrom
    return option.allowNow ? 'now' : DEFAULT_FROM
  }

  const choose = option => {
    setError('')
    setMode(option.mode)
    setFrom(startFor(option))
  }

  const save = async () => {
    setError('')
    try {
      await mutate({ variables: { restaurantId, mode, fromMinute: from === 'now' ? null : from } })
      onSaved && onSaved()
    } catch (e) {
      setError(e?.graphQLErrors?.[0]?.message || e.message || 'Could not save')
    }
  }

  const timeSelect = option => {
    const shown = mode === option.mode ? from : startFor(option)
    const times = typeof shown === 'number' && !TIMES.includes(shown) ? [...TIMES, shown].sort((a, b) => a - b) : TIMES
    return (
      <Select
        size="small"
        value={shown}
        disabled={mode !== option.mode}
        onChange={e => setFrom(e.target.value)}
        MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
        sx={selectSx}
        inputProps={{ 'aria-label': `${option.title} start time` }}
      >
        {option.allowNow && <MenuItem value="now">Now</MenuItem>}
        {times.map(m => <MenuItem key={m} value={m}>{clockLabel(m)}</MenuItem>)}
      </Select>
    )
  }

  return (
    <Box sx={{ mt: 1.5, p: 2, border: `1px solid ${PF.border}`, borderRadius: '10px', bgcolor: PF.card }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: PF.text, mb: 1 }}>When do your own drivers deliver?</Typography>
      <Box role="radiogroup" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {OPTIONS.map(option => (
          <Box
            key={option.mode}
            onClick={() => mode !== option.mode && choose(option)}
            sx={{
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.25, rowGap: 0.5,
              minHeight: 48, px: 1, borderRadius: '8px', cursor: 'pointer',
              bgcolor: mode === option.mode ? PF.onBg : 'transparent',
            }}
          >
            <Radio
              checked={mode === option.mode}
              onChange={() => choose(option)}
              size="small"
              inputProps={{ 'aria-label': option.title }}
              sx={{ p: 0.5, color: PF.inputBorder, '&.Mui-checked': { color: PF.primary } }}
            />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: PF.text, minWidth: 150 }}>{option.title}</Typography>
            <Typography sx={{ fontSize: 14, color: PF.secondary, minWidth: 60 }}>{option.lead}</Typography>
            <Box onClick={e => e.stopPropagation()}>{timeSelect(option)}</Box>
            {option.tail && <Typography sx={{ fontSize: 14, color: PF.secondary }}>{option.tail}</Typography>}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          disabled={loading || !dirty}
          onClick={save}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
        {draft && (
          <Button variant="text" disabled={loading} onClick={onCancel} sx={{ color: PF.secondary }}>Cancel</Button>
        )}
      </Box>
      {error && <Typography sx={{ mt: 1, fontSize: 14, color: '#B42318' }}>{error}</Typography>}
      {!draft && restaurant?.selfDeliverySummary && !dirty && (
        <Typography sx={{ mt: 1.25, fontSize: 14, color: PF.secondary, lineHeight: 1.45 }}>{restaurant.selfDeliverySummary}</Typography>
      )}
    </Box>
  )
}
